import React, { useEffect, useState } from "react";
import { Lightning, Ruler, ShieldCheck, Coins, Wrench, Truck, MapPin, ArrowRight } from "@phosphor-icons/react";

const items = [
  {
    icon: Lightning,
    title: "Gyors kivitelezés",
    short: "Szerződéstől 1 hónap",
    desc: "Szerződéskötéstől számítva 1 hónapon belül kész. Fix dátum a szerződésben — amit aláírunk, az megvalósul.",
    metric: { v: "1", u: "hónap", l: "szerződés → kész" },
  },
  {
    icon: Ruler,
    title: "Egyedi méret",
    short: "Milliméterre az ön telkéhez",
    desc: "3×4-től 12×20 méterig, bármilyen kombináció. Saját üzemünkben gyártjuk — nem típus, nem szabványméret.",
    metric: { v: "240+", u: "m²", l: "max alapterület" },
  },
  {
    icon: ShieldCheck,
    title: "20 év szerkezeti garancia",
    short: "Évtizedekre tervezve",
    desc: "Horganyzott acélváz, PUR vagy kőzetgyapot magú szendvicspanel. Időtálló anyagok, európai gyártás, fenntartható minőség.",
    metric: { v: "20 év", u: "", l: "garancia" },
  },
  {
    icon: Coins,
    title: "Fix ár, meglepetés nélkül",
    short: "A szerződésben szereplő ár az utolsó",
    desc: "Nincs pótszámla, nincs rejtett tétel. Tételes kalkuláció, transzparens vállalkozói díj. Ami a papíron van, az fizet.",
    metric: { v: "0 Ft", u: "", l: "rejtett költség" },
  },
  {
    icon: Wrench,
    title: "Kulcsrakész csapat",
    short: "Egy felelős, egy szerződés",
    desc: "Tervezéstől átadásig egy projektmenedzser kíséri a folyamatot. Kőműves, burkoló, villanyszerelő — minden szakember nálunk dolgozik.",
    metric: { v: "1", u: "", l: "kapcsolattartó" },
  },
  {
    icon: Truck,
    title: "Szállítás országosan",
    short: "Saját flotta minden megyébe",
    desc: "Budapesttől a határig, saját flotta, saját szerelőbrigád. A logisztika sosem akadály.",
    metric: { v: "19", u: "", l: "megye" },
  },
  {
    icon: MapPin,
    title: "Egyszerű bejelentés",
    short: "36 m² alatt csak bejelentés",
    desc: "A 36 m² alatti garázs nem engedélyköteles — elég egy egyszerű bejelentés. 36 m² felett az ügyintézést Önre bízzuk, így nincs felesleges kör.",
    metric: { v: "36 m²", u: "", l: "alatt nincs engedély" },
  },
];

export default function USP() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => setActive((a) => (a + 1) % items.length), 5500);
    return () => clearInterval(id);
  }, [paused, active]);

  const Icon = items[active].icon;
  const item = items[active];

  return (
    <section id="usp" data-testid="usp-section" className="py-20 md:py-32 bg-[#F5F5F5]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid md:grid-cols-2 gap-8 mb-12 md:mb-16 items-end">
          <div>
            <span className="badge-chip" style={{ background: "#0F0F0F", color: "#FFFFFF" }}>
              Miért minket válasszon
            </span>
            <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tight mt-4 leading-[0.95]">
              Hét érv,<br/>ami meggyőzi.
            </h2>
          </div>
          <p className="text-[#525252] text-base md:text-lg max-w-md md:justify-self-end">
            Nem üres ígéretek, csak konkrét számok. Kattintson bármelyik pontra — vagy hagyja, hogy automatikusan végigmenjünk rajtuk.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-px bg-[#E4E4E7] border border-[#E4E4E7] min-h-[500px]"
             onMouseEnter={() => setPaused(true)}
             onMouseLeave={() => setPaused(false)}>
          {/* Left: list */}
          <div className="lg:col-span-5 bg-[#FFFFFF]">
            <ul>
              {items.map((it, i) => {
                const ItemIcon = it.icon;
                const isActive = i === active;
                return (
                  <li key={i}>
                    <button
                      data-testid={`usp-item-${i}`}
                      onMouseEnter={() => setActive(i)}
                      onClick={() => setActive(i)}
                      className={`w-full text-left flex items-center gap-4 p-5 md:p-6 border-b border-[#E4E4E7] transition-all relative group ${
                        isActive ? "bg-[#0F0F0F] text-[#FFFFFF]" : "bg-[#FFFFFF] text-[#0F0F0F] hover:bg-[#F5F5F5]"
                      }`}
                    >
                      <span className={`font-display font-black text-2xl md:text-3xl w-10 shrink-0 ${isActive ? "text-[#FFFFFF]" : "text-[#E4E4E7]"}`}>
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <ItemIcon size={20} weight={isActive ? "fill" : "duotone"} className={isActive ? "text-[#FFFFFF]" : "text-[#0F0F0F]"} />
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-bold text-base md:text-lg leading-tight">{it.title}</div>
                        <div className={`text-xs md:text-sm mt-0.5 ${isActive ? "text-[#E4E4E7]" : "text-[#525252]"} truncate`}>{it.short}</div>
                      </div>
                      <ArrowRight
                        size={18}
                        weight="bold"
                        className={`shrink-0 transition-all ${isActive ? "text-[#FFFFFF] translate-x-1" : "text-[#E4E4E7] -translate-x-2 opacity-0 group-hover:opacity-100 group-hover:translate-x-0"}`}
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Right: featured panel */}
          <div className="lg:col-span-7 bg-white p-8 md:p-12 lg:p-14 flex flex-col justify-between relative overflow-hidden">
            <div className="absolute -top-16 -right-16 watermark text-[280px] select-none pointer-events-none" style={{ color: "rgba(42,36,24,0.04)" }}>
              {String(active + 1).padStart(2, "0")}
            </div>

            <div className="relative slide-up" key={active}>
              <div className="w-16 h-16 bg-[#0F0F0F] flex items-center justify-center rounded-sm mb-6">
                <Icon size={28} weight="duotone" className="text-[#FFFFFF]" />
              </div>
              <div className="text-[10px] font-mono text-[#0F0F0F] uppercase tracking-[0.2em] mb-3">
                {String(active + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
              </div>
              <h3 className="font-display font-black text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[0.95] mb-4">
                {item.title}
              </h3>
              <p className="text-base md:text-lg text-[#525252] leading-relaxed max-w-lg mb-8">
                {item.desc}
              </p>
            </div>

            <div className="relative flex items-end justify-between gap-6 pt-8 border-t border-[#E4E4E7]">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#525252] mb-1">Bizonyíték</div>
                <div className="flex items-baseline gap-1">
                  <span className="number-huge text-4xl md:text-5xl text-[#0F0F0F]">{item.metric.v}</span>
                  {item.metric.u && <span className="font-display font-bold text-lg text-[#0F0F0F]">{item.metric.u}</span>}
                </div>
                <div className="text-xs text-[#525252] mt-1">{item.metric.l}</div>
              </div>
              <a href="#quote" className="cta-primary text-sm">
                Kérek ajánlatot <ArrowRight size={16} weight="bold" />
              </a>
            </div>

            {/* Auto-advance progress bar */}
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#E4E4E7]">
              <div
                key={`pb-${active}`}
                className="h-full bg-[#DC2626]"
                style={{ animation: "fillBar 5500ms linear" }}
              />
            </div>
            <style>{`@keyframes fillBar { from { width: 0% } to { width: 100% } }`}</style>
          </div>
        </div>
      </div>
    </section>
  );
}
